import { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from "react";
import { useLazySearchClubMembersForMentionQuery } from "@/api/clubsApi";
import { Avatar, AvatarImage, AvatarFallback } from "./Avatar";
import { Icon } from "./Icon";
import { cn } from "./cn";
import { debounce } from "radash";

/** User data returned from mention search */
export interface MentionUser {
  user_id: string;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  avatar_small: string | null;
}

interface MentionAutocompletePanelProps {
  clubId: string;
  searchQuery: string;
  currentUserId: string;
  onSelect: (user: MentionUser) => void;
  onClose: () => void;
  visible: boolean;
  position?: "above" | "below";
  /** Optional post ID to prioritize conversation participants in results */
  contextPostId?: string;
  className?: string;
}

/** Handle exposed by MentionAutocompletePanel for imperative actions */
export interface MentionAutocompletePanelHandle {
  /** Select the currently highlighted result, returns true if selection was made */
  selectHighlighted: () => boolean;
  /** Move highlight up, returns true if moved */
  moveUp: () => boolean;
  /** Move highlight down, returns true if moved */
  moveDown: () => boolean;
  /** @deprecated Use selectHighlighted instead */
  selectFirst: () => boolean;
}

const AVATAR_SIZE = 40;
const MAX_VISIBLE_ITEMS = 4;

export const MentionAutocompletePanel = forwardRef<
  MentionAutocompletePanelHandle,
  MentionAutocompletePanelProps
>(
  (
    {
      clubId,
      searchQuery,
      currentUserId,
      onSelect,
      onClose,
      visible,
      position = "below",
      contextPostId,
      className,
    },
    ref
  ) => {
    const [triggerSearch, { data: searchResults, isFetching }] =
      useLazySearchClubMembersForMentionQuery();

    // Debounced search function for typed queries
    const debouncedSearch = useMemo(
      () =>
        debounce({ delay: 300 }, (query: string) => {
          triggerSearch({ clubId, query, currentUserId, postId: contextPostId });
        }),
      [clubId, currentUserId, contextPostId, triggerSearch]
    );

    // Trigger search when query changes
    useEffect(() => {
      if (visible) {
        if (searchQuery.length === 0) {
          // Show initial members immediately when just "@" is typed
          triggerSearch({ clubId, query: "", currentUserId, postId: contextPostId });
        } else {
          // Debounce for typed queries
          debouncedSearch(searchQuery);
        }
      }
    }, [visible, searchQuery, debouncedSearch, triggerSearch, clubId, currentUserId, contextPostId]);

    // Memoize results to avoid useImperativeHandle re-running on every render
    const results = useMemo(() => searchResults || [], [searchResults]);

    // Track highlighted index for keyboard navigation
    const [highlightedIndex, setHighlightedIndex] = useState(0);

    // Reset highlighted index when results change
    useEffect(() => {
      setHighlightedIndex(0);
    }, [results]);

    // Expose imperative methods via ref
    useImperativeHandle(
      ref,
      () => ({
        selectHighlighted: () => {
          if (results.length > 0 && highlightedIndex < results.length) {
            onSelect(results[highlightedIndex]);
            return true;
          }
          return false;
        },
        moveUp: () => {
          if (results.length > 0) {
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
            return true;
          }
          return false;
        },
        moveDown: () => {
          if (results.length > 0) {
            setHighlightedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
            return true;
          }
          return false;
        },
        // Deprecated: use selectHighlighted
        selectFirst: () => {
          if (results.length > 0 && highlightedIndex < results.length) {
            onSelect(results[highlightedIndex]);
            return true;
          }
          return false;
        },
      }),
      [results, highlightedIndex, onSelect]
    );

    // Don't render if not visible
    if (!visible) {
      return null;
    }

    // Don't render if no results and not fetching (query returned no matches)
    if (results.length === 0 && !isFetching) {
      return null;
    }

    // Get display name from user data
    const getDisplayName = (user: MentionUser): string => {
      return (
        user.display_name ||
        [user.first_name, user.last_name].filter(Boolean).join(" ") ||
        user.username ||
        "Unknown User"
      );
    };

    // Get initials for fallback avatar
    const getInitials = (name: string): string => {
      return name
        .split(" ")
        .map((part) => part[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    };

    return (
      <div
        className={cn(
          "absolute left-0 right-0 z-50",
          position === "above" ? "bottom-full mb-2" : "top-full mt-2",
          className
        )}
      >
        <div
          className={cn(
            "bg-background border border-border-tertiary rounded-2xl overflow-hidden shadow-lg",
            "animate-in fade-in-0 zoom-in-95 duration-100"
          )}
          style={{
            maxHeight: `${MAX_VISIBLE_ITEMS * 56 + 32}px`, // 56px per item + 32px padding
          }}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-surface-2 text-text-tertiary hover:text-text-secondary transition-colors z-10"
          >
            <Icon name="xmark" size={12} />
          </button>

          {/* Loading state */}
          {results.length === 0 && isFetching ? (
            <div className="flex items-center justify-center py-6 px-4">
              <div className="w-5 h-5 border-2 border-text-tertiary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            /* Results list */
            <div
              className="overflow-y-auto p-2"
              style={{ maxHeight: `${MAX_VISIBLE_ITEMS * 56 + 8}px` }}
            >
              {results.map((user, index) => {
                const displayName = getDisplayName(user);
                const initials = getInitials(displayName);
                const isHighlighted = index === highlightedIndex;

                return (
                  <button
                    key={user.user_id}
                    type="button"
                    onClick={() => onSelect(user)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={cn(
                      "w-full flex items-center gap-3 p-2 rounded-xl text-left transition-colors",
                      isHighlighted && "bg-surface"
                    )}
                  >
                    <Avatar
                      style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
                    >
                      {user.avatar_small && (
                        <AvatarImage src={user.avatar_small} alt={displayName} />
                      )}
                      <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-body-2-medium text-text-primary truncate">
                        {displayName}
                      </p>
                      {user.username && (
                        <p className="text-body-3 text-text-tertiary truncate">
                          @{user.username}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }
);

MentionAutocompletePanel.displayName = "MentionAutocompletePanel";

export default MentionAutocompletePanel;
